import { FormContainer } from "@/components/form/FormContainer";
import { useResourceOne, useResourceUpdate } from "@/hooks";
import { useResourceNavigate } from "@/hooks/useResourceNavigate";
import { useResourceContext } from "@/layouts/ResourceContext";
import { UpdateWrapper } from "@/layouts/UpdateWrapper";
import { RESOURCE_TYPE_INSTANCES, RESOURCE_TYPE_USERS } from "@/resources-def";
import { Button, Space } from "antd";
import { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useList } from "@refinedev/core";

const RESOURCE = RESOURCE_TYPE_USERS;

export const UserEditor = () => {
  const formRef = useRef();
  const navigate = useResourceNavigate();
  const navigateBack = useNavigate();
  const { data, isLoading, isError } = useResourceOne();
  const oriInstances = data?.instances?.map(instance => ({id: instance})) || [];
  const { mutateAsync, isLoading: isSaving } = useResourceUpdate();

  const { tenantName } = useResourceContext();

  const {data: instanceList, loading} = useList({
    resource: RESOURCE_TYPE_INSTANCES,
  })
  
  const [instances, setInstances] = useState({}); 

  const isValueValid = (val) => instances[val];

  useEffect(() => {
    if(instanceList) {
      const v = instanceList.data.reduce((accumulator, item) => {
        if(item.tenant == tenantName) {
          accumulator[item.id] = { text: item.name };
        }
        return accumulator;
      }, {});
      setInstances(v);
    }
  },[instanceList]);

  const tagRender = (props) => (
    <span 
      style={{ 
        color: isValueValid(props.value) ? 'inherit' : 'red',
        marginRight: 4,
        display: 'inline-block'
      }}
    >
      {props.label}
    </span>
  )

  const rawSchema = [
    {
      i18nTitle: "config.tenants.form.name",
      title: "User Name",
      dataIndex: "name",
      width: "md",
      fieldProps: {
        disabled: true,
      },
    },
    {
      i18nTitle: "users.fields.email",
      title: "Email",
      dataIndex: "email",
      width: "md",
      formItemProps: {
        rules: [
          {
            required: true,
            message: `Email is required.`,
          },
        ]
      }
    },
    {
      i18nTitle: "users.fields.fullname",
      title: "Full Name",
      dataIndex: "fullname",
      width: "md",
    },
    {
      i18nTitle: "user.fields.instances",
      title: "Instances",
      dataIndex: "instances",
      valueType: "select",
      valueEnum: instances,
      width: "328px",
      fieldProps: {
        mode: 'multiple',
        loading: isLoading,
        tagRender: tagRender
      },
    },
    {
      i18nTitle: "users.fields.password",
      title: "Password",
      dataIndex: "password",
      valueType: "password",
      width: "md",
      fieldProps: {
        autoComplete: "new-password",
      },
    },
  ];

  if (tenantName != "__root__") {
    rawSchema.push({
      i18nTitle: "users.form.isAdmin",
      title: "Admin",
      dataIndex: "isAdmin",
      width: "md",
      valueType: "switch",
    });
  } else {
    rawSchema.push({
      i18nTitle: "users.form.storage",
      title: "Store in Cognito",
      dataIndex: "storeInCognito",
      width: "md",
      valueType: "switch",
    });
  }

  const prepareInstances = (items) => {
    const adds = items.filter(instance => {
      return !oriInstances.some(oriInstance => oriInstance.id === instance)
    });
    const removes = oriInstances.filter(oriInstance => {
      return !items.some(instance => instance === oriInstance.id);
    })
    let result = adds.join(",");
    if(removes?.length > 0) {
      if(result.length > 0) {
        result += ",";
      }
      result += removes.map(remove => "-" + remove.id).join(",");
    }
    return result;
  }

  const doCommit = async (values) => {
    values["instances"] = prepareInstances(values["instances"]);
    const result = await mutateAsync({ data: values });
    if (result) {
      navigate({ resource: RESOURCE, action: "list" });
    } else {
      // TODO: show error message
    }
  };

  return (
    <UpdateWrapper
      footer={
        <Space>
          <Button
            onClick={() => {
              navigate({ resource: RESOURCE, action: "list" });
            }}
          >
            Cancel
          </Button>
          <Button loading={isSaving} type="primary" onClick={() => formRef.current.submit()}>
            Save
          </Button>
        </Space>
      }
    >
      {isLoading && <div>Loading...</div>}
      {!isLoading && (
        <FormContainer
          formRef={formRef}
          initValue={data}
          schema={rawSchema}
          onFinishCallback={doCommit}
          style={{ paddingTop: 24 }}
        />
      )}
    </UpdateWrapper>
  );
};
